import {
  getWarningLevel,
  sortWarningsBySeverity,
} from '@src/shared/lib/warnings';

import type {
  UserProfileSection,
  UserUpdateDto,
  UserWarningDefinitionDto,
  UserWarningDefinitionsDto,
  UserWarningDto,
  UserWarningEvaluatePayload,
  UserWarningListSummaryDto,
} from '../types';

// Every user field a form can send. A page that shows warnings by section
// has to place each of them, so its map is complete by construction.
export type UserWarningDisplayField = Exclude<keyof UserUpdateDto, 'salary_id'>;

// Where a page shows each field. Pages group the fields differently (the
// profile and the Add User wizard do), so each page has a map of its own.
export type UserWarningSectionMap<S extends string> = Readonly<
  Record<UserWarningDisplayField, S>
>;

// A section map as the functions below read it: by any field name, since
// the server may name a field this client does not know yet.
type SectionByField<S extends string> = Readonly<Record<string, S>>;

// How the user profile groups the fields into its sections.
export const USER_PROFILE_SECTION_BY_FIELD: UserWarningSectionMap<UserProfileSection> =
  {
    email: 'contacts',
    language: 'security',
    salary_saly_end: 'career',
    salary_saly_hourly: 'career',
    salary_saly_monthly: 'career',
    salary_saly_monthly_salary: 'career',
    salary_saly_percentage: 'career',
    salary_saly_rank: 'career',
    salary_saly_start: 'career',
    user_address_line_1: 'location',
    user_address_line_2: 'location',
    user_birthdate: 'personal',
    user_bran_workplace: 'professional',
    user_city: 'location',
    user_ctry: 'location',
    user_dept: 'professional',
    user_email_private: 'contacts',
    user_emergency_contact1: 'emergency',
    user_emergency_contact2: 'emergency',
    user_emergency_email1: 'emergency',
    user_emergency_email2: 'emergency',
    user_emergency_mobile1: 'emergency',
    user_emergency_mobile2: 'emergency',
    user_emergency_relationship1: 'emergency',
    user_emergency_relationship2: 'emergency',
    user_family_name: 'personal',
    user_first_name: 'personal',
    user_initials: 'personal',
    user_joti: 'professional',
    user_mobile: 'contacts',
    user_mobile_private: 'contacts',
    user_phone: 'contacts',
    user_phone_private: 'contacts',
    user_portrait: 'personal',
    user_postcode: 'location',
    user_signature: 'personal',
    user_team: 'professional',
    user_titl: 'personal',
    user_user_deputy: 'professional',
    user_user_hr: 'professional',
    user_user_manager: 'professional',
  };

// Fields the warning payload builders actually send. Live evaluation can only
// react to these, so they are also the only trigger fields the client supports.
export const USER_WARNING_PAYLOAD_FIELDS = [
  'email',
  'user_initials',
  'user_birthdate',
  'user_email_private',
  'user_mobile_private',
  'user_user_manager',
  'user_user_hr',
  'user_user_deputy',
  'user_emergency_contact1',
  'user_emergency_mobile1',
  'user_emergency_email1',
  'user_address_line_1',
  'user_city',
  'user_postcode',
] as const satisfies readonly (keyof UserWarningEvaluatePayload)[];

const USER_WARNING_PRESENCE_FIELDS = [
  'portrait_present',
  'signature_present',
] as const satisfies readonly (keyof UserWarningEvaluatePayload)[];

const USER_WARNING_TRIGGER_FIELDS: ReadonlySet<string> = new Set([
  ...USER_WARNING_PAYLOAD_FIELDS,
  ...USER_WARNING_PRESENCE_FIELDS,
]);

const reportedDefinitionIssues = new Set<string>();

type WarningDefinitionValidationOptions = {
  report?: (message: string) => void;
  // The map of the page that is going to show the warnings.
  sectionByField: SectionByField<string>;
  strict?: boolean;
};

export function getUserWarningDefinitionIssues(
  payload: UserWarningDefinitionsDto,
  sectionByField: SectionByField<string>,
): string[] {
  const issues = new Set<string>();

  payload.definitions.forEach((definition) => {
    definition.fields.forEach((field) => {
      if (!Object.hasOwn(sectionByField, field)) {
        issues.add(
          `Warning definition "${definition.code}" has an unmapped display field "${field}".`,
        );
      }
    });
    definition.trigger_fields.forEach((field) => {
      if (!USER_WARNING_TRIGGER_FIELDS.has(field)) {
        issues.add(
          `Warning definition "${definition.code}" has an unknown trigger field "${field}".`,
        );
      }
    });
  });

  Object.keys(payload.triggers).forEach((field) => {
    if (!USER_WARNING_TRIGGER_FIELDS.has(field)) {
      issues.add(
        `Warning triggers contain an unknown payload field "${field}".`,
      );
    }
  });

  return [...issues].sort();
}

export function validateUserWarningDefinitions(
  payload: UserWarningDefinitionsDto,
  options: WarningDefinitionValidationOptions,
): UserWarningDefinitionsDto {
  const issues = getUserWarningDefinitionIssues(
    payload,
    options.sectionByField,
  );
  if (issues.length === 0) {
    return payload;
  }

  const message = `Invalid user warning definitions:\n${issues.join('\n')}`;
  // Report before throwing: the query layer keeps an error thrown from
  // `select` in its error state without logging it.
  if (!reportedDefinitionIssues.has(message)) {
    reportedDefinitionIssues.add(message);
    (options.report ?? console.error)(message);
  }

  const strict = options.strict ?? !import.meta.env.PROD;
  if (strict) {
    throw new Error(message);
  }
  return payload;
}

// The sections a page shows the given fields in. A field the page's map does
// not know belongs to no section.
export function getUserWarningSections<S extends string>(
  fields: readonly string[],
  sectionByField: SectionByField<S>,
): Set<S> {
  return new Set(
    fields.flatMap((field) =>
      Object.hasOwn(sectionByField, field) ? [sectionByField[field]] : [],
    ),
  );
}

export function isUserWarningInSection<S extends string>(
  warning: UserWarningDto,
  section: S,
  sectionByField: SectionByField<S>,
) {
  return getUserWarningSections(warning.fields, sectionByField).has(section);
}

// Codes of definitions whose trigger fields all still hold their last
// evaluated value, so their previously shown result is current.
export function getUnchangedUserWarningCodes(
  definitions: readonly UserWarningDefinitionDto[],
  changedFields: ReadonlySet<string>,
): string[] {
  return definitions
    .filter((definition) =>
      definition.trigger_fields.every((field) => !changedFields.has(field)),
    )
    .map((definition) => definition.code);
}

export function getUserWarningDefinitionsInSections(
  definitions: readonly UserWarningDefinitionDto[],
  sections: ReadonlySet<string>,
  sectionByField: SectionByField<string>,
): UserWarningDefinitionDto[] {
  return definitions.filter((definition) =>
    [...getUserWarningSections(definition.fields, sectionByField)].some(
      (section) => sections.has(section),
    ),
  );
}

export function getUserWarningTriggerFields(
  definitions: readonly UserWarningDefinitionDto[],
): string[] {
  return [
    ...new Set(definitions.flatMap((definition) => definition.trigger_fields)),
  ];
}

const USER_WARNING_DEFINITIONS_RETRY_COOLDOWN_MS = 30_000;

// Loads the definitions when a check needs them and they are missing (still
// on their way, or the request failed). After a failed attempt it does not
// ask again for a while, so a server that is down is not hit by every field
// the user leaves. An empty list of definitions is a valid answer.
export function createUserWarningDefinitionsLoader({
  cooldownMs = USER_WARNING_DEFINITIONS_RETRY_COOLDOWN_MS,
  fetch,
  now = Date.now,
}: {
  cooldownMs?: number;
  fetch: () => Promise<UserWarningDefinitionsDto | undefined>;
  now?: () => number;
}) {
  let lastFailureAt: number | null = null;

  return async (
    loaded: UserWarningDefinitionsDto | undefined,
  ): Promise<UserWarningDefinitionsDto | undefined> => {
    if (loaded) {
      return loaded;
    }
    if (lastFailureAt !== null && now() - lastFailureAt < cooldownMs) {
      return undefined;
    }
    const definitions = await fetch();
    lastFailureAt = definitions ? null : now();
    return definitions;
  };
}

// Uploads are evaluated through a presence flag, not through the file itself.
const USER_WARNING_TRIGGER_FIELD_BY_DISPLAY_FIELD: Record<string, string> = {
  user_portrait: 'portrait_present',
  user_signature: 'signature_present',
};

// Fallbacks for the time the definitions are unavailable (still loading, or
// the request failed). The trigger fields of a warning are then unknown, and
// the fields it is shown on are the closest known substitute.
export function getUserWarningFallbackTriggerFields(
  warning: Pick<UserWarningDto, 'fields'>,
): string[] {
  return warning.fields.map(
    (field) => USER_WARNING_TRIGGER_FIELD_BY_DISPLAY_FIELD[field] ?? field,
  );
}

export function getUserWarningCodesByFallbackField(
  warnings: readonly UserWarningDto[],
  triggerField: string,
): string[] {
  return [
    ...new Set(
      warnings
        .filter((warning) =>
          getUserWarningFallbackTriggerFields(warning).includes(triggerField),
        )
        .map((warning) => warning.code),
    ),
  ];
}

// Rebuilds the last-evaluated payload after persisted data or the set of
// draft sections changes. Trigger fields of sections that are still being
// edited keep their previous evaluated values: taking the current draft
// values instead would mark an evaluation that never completed as done.
export function rebaseUserWarningEvaluatedPayload(
  persistedPayload: UserWarningEvaluatePayload,
  previousEvaluatedPayload: UserWarningEvaluatePayload,
  draftSections: ReadonlySet<string>,
  definitions: readonly UserWarningDefinitionDto[],
  sectionByField: SectionByField<string>,
): UserWarningEvaluatePayload {
  const next = { ...persistedPayload };
  const draftTriggerFields = getUserWarningTriggerFields(
    getUserWarningDefinitionsInSections(
      definitions,
      draftSections,
      sectionByField,
    ),
  );

  draftTriggerFields.forEach((field) => {
    const key = field as keyof UserWarningEvaluatePayload;
    if (Object.hasOwn(previousEvaluatedPayload, key)) {
      Object.assign(next, { [key]: previousEvaluatedPayload[key] });
    } else {
      delete next[key];
    }
  });
  return next;
}

// Decides which parts of an evaluate response may still be applied when it
// arrives. A code is accepted only if none of its trigger fields changed
// while the request was in flight; a field counts as evaluated only if every
// code it triggers was accepted, so a rejected code is requested again.
//
// A code missing from the cached definitions has unknown trigger fields. The
// server only returns codes triggered by the request's changed fields, so
// such a code is accepted only if all of those are still current; if it is
// rejected, no field of the request counts as evaluated.
export function resolveUserWarningEvaluation({
  changedFields,
  definitions,
  evaluatedCodes,
  isFieldCurrent,
}: {
  changedFields: readonly string[];
  definitions: readonly UserWarningDefinitionDto[];
  evaluatedCodes: readonly string[];
  isFieldCurrent: (field: string) => boolean;
}): { acceptedCodes: Set<string>; acceptedFields: string[] } {
  const definitionByCode = new Map(
    definitions.map((definition) => [definition.code, definition]),
  );
  const allChangedFieldsCurrent = changedFields.every(isFieldCurrent);
  const acceptedCodes = new Set(
    evaluatedCodes.filter((code) => {
      const definition = definitionByCode.get(code);
      return definition
        ? definition.trigger_fields.every(isFieldCurrent)
        : allChangedFieldsCurrent;
    }),
  );
  const hasRejectedUnknownCode = evaluatedCodes.some(
    (code) => !definitionByCode.has(code) && !acceptedCodes.has(code),
  );
  const acceptedFields = hasRejectedUnknownCode
    ? []
    : changedFields.filter(
        (field) =>
          isFieldCurrent(field) &&
          definitions
            .filter((definition) => definition.trigger_fields.includes(field))
            .every((definition) => acceptedCodes.has(definition.code)),
      );

  return { acceptedCodes, acceptedFields };
}

// Records what a failed save evaluated. The server computes the warnings of
// the submitted draft, but falls back to the saved value for every field it
// rejected. Such a field counts as evaluated with its saved value, and the
// codes it triggers are stale while the draft holds something else.
export function resolveUserWarningDraftResult({
  definitions,
  draftValues,
  persistedValues,
  unevaluatedFields,
}: {
  definitions: readonly UserWarningDefinitionDto[];
  draftValues: UserWarningEvaluatePayload;
  persistedValues: UserWarningEvaluatePayload;
  unevaluatedFields: readonly string[];
}): { evaluatedValues: UserWarningEvaluatePayload; staleCodes: string[] } {
  const rejectedFields = new Set(unevaluatedFields);
  const isDraftUnevaluated = (field: string) => {
    const key = field as keyof UserWarningEvaluatePayload;
    return (
      rejectedFields.has(field) && draftValues[key] !== persistedValues[key]
    );
  };
  const evaluatedValues: UserWarningEvaluatePayload = {};
  getUserWarningTriggerFields(definitions).forEach((field) => {
    const key = field as keyof UserWarningEvaluatePayload;
    const source = rejectedFields.has(field) ? persistedValues : draftValues;
    Object.assign(evaluatedValues, { [key]: source[key] });
  });

  return {
    evaluatedValues,
    staleCodes: definitions
      .filter((definition) =>
        definition.trigger_fields.some(isDraftUnevaluated),
      )
      .map((definition) => definition.code),
  };
}

export function isUserWarningDefinitionInSection<S extends string>(
  definition: UserWarningDefinitionDto,
  section: S,
  sectionByField: SectionByField<S>,
) {
  return getUserWarningSections(definition.fields, sectionByField).has(section);
}

export function getUserWarningLevel(
  warnings: readonly UserWarningDto[],
): UserWarningListSummaryDto['level'] {
  return getWarningLevel(warnings);
}

export function buildUserWarningSummary(
  warnings: readonly UserWarningDto[],
): UserWarningListSummaryDto {
  const sortedWarnings = sortWarningsBySeverity(warnings);
  return {
    level: getUserWarningLevel(sortedWarnings),
    count: sortedWarnings.length,
    warnings: sortedWarnings,
  };
}

// Groups the warnings by the sections of a page, red ones first.
export function buildUserWarningSections<S extends string>(
  warnings: readonly UserWarningDto[],
  sectionByField: SectionByField<S>,
  visibleSections?: ReadonlySet<S>,
): Partial<Record<S, UserWarningListSummaryDto>> {
  const sections: Partial<Record<S, UserWarningListSummaryDto>> = {};

  sortWarningsBySeverity(warnings).forEach((warning) => {
    getUserWarningSections(warning.fields, sectionByField).forEach(
      (section) => {
        if (visibleSections && !visibleSections.has(section)) {
          return;
        }
        const current = sections[section];
        const sectionWarnings = current
          ? [...current.warnings, warning]
          : [warning];
        sections[section] = {
          level: getUserWarningLevel(sectionWarnings),
          count: sectionWarnings.length,
          warnings: sectionWarnings,
        };
      },
    );
  });

  return sections;
}
